/*
 * Product analytics is deliberately a small, default-off boundary.  This file
 * never imports the browser SDK until both build-time settings opt in.
 *
 * The pinned 3.4.4 source verifies the option names used here. The privacy
 * test specification remains the activation gate; this adapter alone is not
 * evidence that an SDK envelope is safe.
 */

export const PRODUCT_ANALYTICS_SCHEMA_VERSION = "1";

export const PRODUCT_ANALYTICS_SECTIONS = new Set([
  "overview", "queue", "recs", "profile", "azure", "support", "admin", "licensing",
  "customer_selection",
]);

export const PRODUCT_ANALYTICS_ACTIONS = new Set([
  "scan_requested",
  "review_booking_requested",
  "support_submitted",
  "executive_report_download_requested",
  "findings_export_requested",
]);

const EVENT_NAMES = new Set([
  "portal_section_visit",
  "portal_navigation",
  "portal_active_time",
  "portal_action",
]);

const AUDIENCES = new Set(["staff", "customer"]);
const MAX_PENDING_EVENTS = 50;

export const isProductAnalyticsConfigured = () =>
  process.env.REACT_APP_PRODUCT_ANALYTICS_ENABLED === "true"
  && typeof process.env.REACT_APP_PRODUCT_ANALYTICS_CONNECTION_STRING === "string"
  && process.env.REACT_APP_PRODUCT_ANALYTICS_CONNECTION_STRING.trim().length > 0;

const validAudience = (audience) => AUDIENCES.has(audience);
const validSection = (section) => PRODUCT_ANALYTICS_SECTIONS.has(section);

export function makeProductAnalyticsEvent(name, { audience, section, fromSection, toSection, action, activeMs } = {}) {
  if (!EVENT_NAMES.has(name) || !validAudience(audience)) return null;

  const properties = {
    schema_version: PRODUCT_ANALYTICS_SCHEMA_VERSION,
    audience,
  };
  const measurements = {};

  if (name === "portal_section_visit" || name === "portal_active_time") {
    if (!validSection(section)) return null;
    properties.section = section;
  } else if (name === "portal_navigation") {
    if (!validSection(fromSection) || !validSection(toSection) || fromSection === toSection) return null;
    properties.from_section = fromSection;
    properties.to_section = toSection;
  } else if (name === "portal_action") {
    if (!validSection(section) || !PRODUCT_ANALYTICS_ACTIONS.has(action)) return null;
    properties.section = section;
    properties.action = action;
  }

  if (name === "portal_active_time") {
    if (!Number.isFinite(activeMs) || activeMs <= 0) return null;
    const wholeMs = Math.floor(activeMs);
    if (wholeMs <= 0) return null;
    measurements.active_ms = wholeMs;
  }

  return { name, properties, measurements };
}

let analytics = null;
let loading = null;
let pending = [];

const discardSdkContext = (envelope) => {
  // This is deliberately restrictive.  Recreate only the EventData contract,
  // then discard SDK-populated identity, operation, URL, extension and tag
  // fields. Exact 3.4.4 envelope shape remains an installation-time check.
  if (!envelope || envelope.baseType !== "EventData") return false;
  const event = makeProductAnalyticsEvent(envelope.baseData?.name, {
    audience: envelope.baseData?.properties?.audience,
    section: envelope.baseData?.properties?.section,
    fromSection: envelope.baseData?.properties?.from_section,
    toSection: envelope.baseData?.properties?.to_section,
    action: envelope.baseData?.properties?.action,
    activeMs: envelope.baseData?.measurements?.active_ms,
  });
  if (!event) return false;
  envelope.tags = {};
  envelope.ext = {};
  envelope.data = {};
  envelope.baseData = {
    name: event.name,
    properties: event.properties,
    measurements: event.measurements,
  };
  return true;
};

const flushPending = () => {
  if (!analytics) return;
  const events = pending;
  pending = [];
  events.forEach(event => {
    try { analytics.trackEvent(event); } catch { /* analytics must never affect the portal */ }
  });
};

export const startProductAnalytics = () => {
  if (!isProductAnalyticsConfigured() || loading || analytics) return;
  loading = Promise.all([
    import("@microsoft/applicationinsights-web"),
    import("@microsoft/applicationinsights-core-js"),
  ])
    .then(([{ ApplicationInsights }, { utlDisableStorage }]) => {
      // Exact option names are verified against the installed 3.4.4 source.
      // PropertiesPlugin initializes before AnalyticsPlugin, so this process-
      // level SDK switch must run before ApplicationInsights is constructed.
      utlDisableStorage();
      const instance = new ApplicationInsights({
        config: {
          connectionString: process.env.REACT_APP_PRODUCT_ANALYTICS_CONNECTION_STRING,
          disableAjaxTracking: true,
          disableFetchTracking: true,
          disableExceptionTracking: true,
          enableAutoRouteTracking: false,
          autoTrackPageVisitTime: false,
          disableCookiesUsage: true,
          enableSessionStorageBuffer: false,
          disableDataLossAnalysis: true,
          enableCorsCorrelation: false,
          disableCorrelationHeaders: true,
          isStorageUseDisabled: true,
          loggingLevelTelemetry: 0,
          enableUnhandledPromiseRejectionTracking: false,
          // The Web SKU includes CfgSync with a default CDN endpoint. These
          // exact extension settings stop both its fetch and config events.
          extensionConfig: {
            AppInsightsCfgSyncPlugin: {
              blkCdnCfg: true,
              syncMode: 0,
            },
          },
        },
      });
      instance.addTelemetryInitializer(discardSdkContext);
      instance.loadAppInsights();
      analytics = instance;
      flushPending();
    })
    .catch(() => {
      // Configuration can be present before the dependency is installed. Keep
      // the portal operational and discard observations in that state.
      pending = [];
    })
    .finally(() => { loading = null; });
};

export const emitProductAnalyticsEvent = (name, details) => {
  if (!isProductAnalyticsConfigured()) return;
  const event = makeProductAnalyticsEvent(name, details);
  if (!event) return;
  try {
    if (analytics) analytics.trackEvent(event);
    else {
      if (pending.length < MAX_PENDING_EVENTS) pending.push(event);
      startProductAnalytics();
    }
  } catch {
    // Analytics must be observational only.
  }
};
